import React, { useMemo } from "react";

/**
 * Красивый клиентский экран:
 * - Если есть savedSummary2Data → показ Auftrag
 * - Иначе, если есть savedSummaryData → показ Angebot
 * - Внизу кнопка "Zur Unterschrift"
 *
 * Ожидаемые пропсы:
 * - savedSummaryData, savedSummary2Data (объекты, как у тебя)
 * - onOpenSignature: () => void
 * - styles: CSS-module с классами (см. снизу CSS)
 */
export default function ClientView({
  savedSummaryData,
  savedSummary2Data,
  onOpenSignature,
  styles,
}) {
  const hasAuftrag = !!savedSummary2Data;
  const hasAngebot = !!savedSummaryData;

  const content = useMemo(() => {
    if (hasAuftrag) return renderSavedAuftrag(savedSummary2Data, styles);
    if (hasAngebot) return renderSavedAngebot(savedSummaryData, styles);
    return <p className={styles.noData}>Keine gespeicherte Zusammenfassung verfügbar.</p>;
  }, [hasAuftrag, hasAngebot, savedSummary2Data, savedSummaryData, styles]);

  return (
    <div className={styles.clientView}>
      <div className={styles.clientViewInner}>
        {content}

        <div className={styles.clientCTA}>
          <button
            type="button"
            className={styles["summary-submit"]}
            onClick={onOpenSignature}
          >
            Zur Unterschrift
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================== Рендеры ================== */

function renderSavedAngebot(data, styles) {
  if (!data) return null;

  const {
    firstName, lastName, email, telefon,
    strasse, hausnummer, plz, ort,
    ausbildung,
    preise = [],
    angebot_preise = [],
    code_preise = [],
    ausbildung_theorie,
    ausbildung_praxis,
    angebotId,
    angebotCreatedAt,
  } = data;

  // массив промо-цен: code_preise в приоритете, иначе angebot_preise
  const promoArray = (Array.isArray(code_preise) && code_preise.length) ? code_preise : (angebot_preise || []);

  return (
    <div className={styles.savedSummary}>
      <div className={styles.headerRow}>
        <p className={styles.topline}>
          <b className={styles.blue}>Angebot Nr. </b> {angebotId} | {angebotCreatedAt}
        </p>
      </div>

      <div className={styles.contactBlock}>
        <p>
          <b>{firstName} {lastName}</b><br />
          {email}<br />
          {telefon}<br />
          {[strasse, hausnummer].filter(Boolean).join(" ") || ""}<br />
          {[plz, ort].filter(Boolean).join(" ") || ""}
        </p>
      </div>

      {ausbildung ? (
        <div className={styles.overwievBlock}>
          <h3 className={styles.blue}>Deine Anfrage</h3>
          <p>Führerschein: Klasse {ausbildung.Klasse?.Name || ausbildung.klasse?.name || "unbekannt"}</p>
          <p>Ausbildungsvariante: {ausbildung.Name || ausbildung.name || "nicht angegeben"}</p>
        </div>
      ) : (
        <p>Keine Ausbildungsinformationen gefunden.</p>
      )}

      <p className={styles.intro}>
        Lieber {firstName},<br />
        vielen Dank für deine Anfrage! Gerne übermitteln wir dir dein persönliches Angebot.
      </p>

      <div className={styles.sectionHead}>
        <h2 className={styles.blue}>Dein Angebot</h2>
      </div>

      <p className={styles.sectionLabel}><strong className={styles.orange}>Theoretischer Teil:</strong></p>
      <div className={styles.blockContent}
           dangerouslySetInnerHTML={{ __html: ausbildung_theorie || "<em>-</em>" }} />

      <p className={styles.sectionLabel}><strong className={styles.orange}>Praktischer Teil:</strong></p>
      <div className={styles.blockContent}
           dangerouslySetInnerHTML={{ __html: ausbildung_praxis || "<em>-</em>" }} />

      <div className={styles.sectionHeadCompact}>
        <strong className={styles.blue}>Dein Preis</strong>
      </div>

      <div className={styles.priceContainer}>
        {Array.isArray(preise) && preise.length > 0 ? (
          preise.map((p, index) => {
            const oldPrice = tryNum(p.preis);
            const promo = promoArray[index];
            const newPrice = promo ? tryNum(promo.preis) : null;

            return (
              <div key={index} className={styles.priceItem}>
                <div className={styles.priceTitle}>
                  <strong>{p.kategorieName || "Allgemein"}</strong>
                  {p.kategorieBeschreibung && (
                    <div className={styles.priceDesc}>{p.kategorieBeschreibung}</div>
                  )}
                </div>
                <div className={styles.priceAmountLine}>
                  {newPrice != null ? (
                    <>
                      <span className={styles.oldPrice}>{fmt(oldPrice)} €</span>{" "}
                      <strong className={styles.newPrice}>{fmt(newPrice)} €</strong>
                    </>
                  ) : (
                    <strong className={styles.newPrice}>{fmt(oldPrice)} €</strong>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className={styles.noPrice}>–</div>
        )}
      </div>

      <div className={styles.note}>
        <p className={styles.bold}>Wir garantieren Prüfungstermine!</p>
        <p className={styles.tight}>
          jeden Montag theoretische Prüfung in Baden<br />
          jeden Dienstag theoretische Prüfung in Traiskirchen<br />
          <b>jeden Freitag praktische Prüfung in Baden</b>
        </p>
      </div>
    </div>
  );
}

function renderSavedAuftrag(data, styles) {
  if (!data) return null;

  const {
    firstName, lastName, email, telefon,
    strasse, hausnummer, plz, ort,
    ausbildung,
    preise = [],
    ausgewaehlterPreis,
    ausbildung_theorie,
    ausbildung_praxis,
    angebotId,
    angebotCreatedAt,
  } = data;

  // находим выбранную цену и её категорию
  const selectedPrice = Array.isArray(preise)
    ? preise.find(p => Number(p.preis || p.Preis) === Number(ausgewaehlterPreis))
    : null;

  return (
    <div className={styles.savedSummary}>
      <div className={styles.headerRow}>
        <p className={styles.topline}>
          <b className={styles.orange}>Ausbildungsauftrag Nr. </b> {angebotId} | {angebotCreatedAt}
        </p>
      </div>

      <div className={styles.contactBlock}>
        <p>
          <b>{firstName} {lastName}</b><br />
          {email}<br />
          {telefon}<br />
          {[strasse, hausnummer].filter(Boolean).join(" ") || ""}<br />
          {[plz, ort].filter(Boolean).join(" ") || ""}
        </p>
      </div>

      {ausbildung ? (
        <div className={styles.overwievBlock}>
          <h3 className={styles.blue}>Deine Anfrage</h3>
          <p>Führerschein: Klasse {ausbildung.Klasse?.Name || ausbildung.klasse?.name || "unbekannt"}</p>
          <p>Ausbildungsvariante: {ausbildung.Name || ausbildung.name || "nicht angegeben"}</p>
        </div>
      ) : (
        <p>Keine Ausbildungsinformationen gefunden.</p>
      )}

      <h2 className={styles.blue}>Leistungsverzeichnis</h2>

      <p className={styles.sectionLabel}><strong className={styles.orange}>Theoretischer Teil:</strong></p>
      <div className={styles.blockContent}
           dangerouslySetInnerHTML={{ __html: ausbildung_theorie || "<em>-</em>" }} />

      <p className={styles.sectionLabel}><strong className={styles.orange}>Praktischer Teil:</strong></p>
      <div className={styles.blockContent}
           dangerouslySetInnerHTML={{ __html: ausbildung_praxis || "<em>-</em>" }} />

      <div className={styles.sectionHeadCompact}>
        <strong className={styles.blue}>Dein Preis</strong>
        <p className={styles.muted}>(inkl. Bearbeitungsgebühr, exkl. behördliche Gebühren)</p>
      </div>

      <div className={styles.priceContainer}>
        {selectedPrice ? (
          <div className={styles.priceItem}>
            <div className={styles.priceTitle}>
              <strong>{selectedPrice.kategorieName || "Allgemein"}</strong>
            </div>
            <div className={styles.priceAmountLine}>
              <strong className={styles.newPrice}>
                {fmt(tryNum(selectedPrice.preis || selectedPrice.Preis))} €
              </strong>
            </div>
            {selectedPrice.kategorieBeschreibung && (
              <div className={styles.priceDesc}>{selectedPrice.kategorieBeschreibung}</div>
            )}
          </div>
        ) : ausgewaehlterPreis ? (
          <div className={styles.priceItem}>
            <div className={styles.priceAmountLine}>
              <strong className={styles.newPrice}>{fmt(tryNum(ausgewaehlterPreis))} €</strong>
            </div>
          </div>
        ) : (
          <div className={styles.noPrice}>–</div>
        )}
      </div>
    </div>
  );
}

/* ================== Утилиты ================== */

function tryNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function fmt(n) {
  return Number(n).toLocaleString("de-DE", { minimumFractionDigits: 0 });
}
